var {isMobile} = require('react-device-detect')
var _moment = require("moment");
var _momentTz = require("moment-timezone");

var _moment2 = _interopRequireDefault(_moment);
var _momentTz2 = _interopRequireDefault(_momentTz);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

// UK IANA timezone identifiers
const UK_TIMEZONES = new Set([
  'Europe/London',
  'Europe/Belfast',
  'Europe/Isle_of_Man',
  'Europe/Jersey',
  'Europe/Guernsey',
]);

/**
 * Returns the browser's IANA timezone string.
 */
export function getViewerTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Returns true if the viewer's browser timezone is a UK timezone.
 */
export function isViewerInUK() {
  return UK_TIMEZONES.has(getViewerTimezone());
}

/**
 * Format an event's releaseTime for the viewer's timezone.
 *
 * For UK viewers: treat stored UTC digits as UK display time (moment.utc).
 * For non-UK viewers: correct the stored UK-as-UTC time to true UTC, then
 * convert to the viewer's local timezone.
 */
export function formatEventTimeForViewer(releaseTime, format) {
  if (isViewerInUK()) {
    return _moment2.default.utc(releaseTime).format(format);
  }
  const localTz = getViewerTimezone();
  const isBST = _momentTz2.default.tz(releaseTime, 'Europe/London').isDST();
  const trueUtc = _moment2.default.utc(releaseTime).subtract(isBST ? 1 : 0, 'hours');
  return _momentTz2.default.tz(trueUtc.toISOString(), localTz).format(format);
}

/**
 * " (BST)"-style suffix for the event time, or "" when the event hides its timezone label.
 */
export function formatEventTimezoneSuffix(event) {
  if (event?.hideTimezoneLabel) return '';
  return ` (${getEventTimezoneLabel(event.releaseTime)})`;
}

/**
 * Full date text for events saved with a custom date format, e.g. "19 Nov 2026 at 14:00 (GMT)".
 */
export function formatEventDateLabel(event) {
  return `${formatEventTimeForViewer(event.releaseTime, event.dateFormat)}${formatEventTimezoneSuffix(event)}`;
}

/**
 * Exact date text typed in the HUB, shown as written (no formatting, no timezone suffix),
 * or null when the event uses its date format.
 */
export function getEventDateText(event) {
  const text = typeof event?.customDateText === 'string' ? event.customDateText.trim() : '';
  return text || null;
}

/**
 * Returns the timezone label to display alongside the event time.
 *
 * UK viewers get 'BST' or 'GMT'; non-UK viewers get their local tz abbreviation.
 */
export function getEventTimezoneLabel(releaseTime) {
  if (isViewerInUK()) {
    return _momentTz2.default.tz(releaseTime, 'Europe/London').isDST() ? 'BST' : 'GMT';
  }
  const localTz = getViewerTimezone();
  const isBST = _momentTz2.default.tz(releaseTime, 'Europe/London').isDST();
  const trueUtc = _moment2.default.utc(releaseTime).subtract(isBST ? 1 : 0, 'hours');
  return _momentTz2.default.tz(trueUtc.toISOString(), localTz).format('z');
}

/**
 * Surface/fill color from branding.background — only when type is "color".
 * Image/video backgrounds must not leak their leftover color field into UI chrome.
 */
export function getBrandingBackgroundColor(background) {
  if (!background || background.type !== "color") return "";
  return background.color || "";
}

/**
 * Holding block layout from branding.
 * Accepts left / right / center plus the top-* and bottom-* variants in any
 * casing or separator style (topLeft, top-left, "top left"), and splits them
 * into a horizontal and a vertical alignment.
 */
export function getHoldingBlockLayout(branding) {
  const key = String(branding?.holdingBlockPosition ?? '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  const vertical = key.startsWith('top')
    ? 'top'
    : key.startsWith('bottom')
      ? 'bottom'
      : 'center';
  const horizontal = key.includes('left')
    ? 'left'
    : key.includes('right')
      ? 'right'
      : 'center';

  return {
    horizontal,
    vertical,
    holdingBlockPosition:
      vertical === 'center' ? horizontal : `${vertical}-${horizontal}`,
  };
}

export function getCurrentIndex(time, array) {
  let index = 0;
  array.forEach((el, idx) => {
    if (el.time <= time) {
      index = idx;
    }
  });

  return index;
}

export function checkLayout(a, b) {
  // Create copies to avoid mutating the original arrays
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return JSON.stringify(sortedA) === JSON.stringify(sortedB);
}

//@ Date Manipulation
const pluralRules = new Intl.PluralRules('en-US', {
  type: 'ordinal',
});

const suffixes = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th',
};

const convertToOrdinal = (number) =>
  `${number}${suffixes[pluralRules.select(number)]}`;

const extractValueAndCustomizeDayOfMonth = (part) => {
  if (part.type === 'day') {
    return convertToOrdinal(part.value);
  }
  return part.value;
};

const longEnUSFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export const getFullDate = (date) => {
  const releaseTime = new Date(date);
  const newDate = new Date(
    releaseTime.toISOString().split('T')[0].split('-').join(',')
  );

  return longEnUSFormatter
    .formatToParts(newDate)
    .map(extractValueAndCustomizeDayOfMonth)
    .join('')
    .split(',')
    .join(' ');
};

export const getTime = (date) => {
  const newDate = new Date(date).toLocaleString(undefined, {
    timeZone: 'Europe/London',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  let time = newDate.split(' ')[0];
  if (time.charAt(0) === '0') {
    time = time.replace('0', '12');
  }

  return {
    time: time,
    suffix: newDate.split(' ')[1],
  };
};

export const getBSTorGMT = (date) => {
  const startOfBST = new Date('2022-03-27T01:00:00.000Z').getTime();
  const endOfBST = new Date('2022-10-30T01:00:00.000Z').getTime();
  const release = new Date(date).getTime();

  if (release > startOfBST && release < endOfBST) {
    return 'BST';
  } else {
    return 'GMT';
  }
};

export const styleReleaseDate = (releaseDate, dateFormat) => {
  const londonTime = getTime(dateFormat);

  const regChar = new RegExp(/(-|,)/g);

  const dayWithComma = `${releaseDate.replaceAll(regChar, '').split(' ')[0]},`;

  let onlyDate = releaseDate
    .replaceAll(regChar, '')
    .split(' ')
    .slice(1, -2)
    .join(' ');

  const onlyDateWithComma = `${dayWithComma} ${onlyDate}`;

  if (releaseDate.includes('/')) {
    onlyDate = releaseDate.split(' ').slice(0, -3).join(' ');
  }

  return {
    onlyDate: onlyDateWithComma,
    hrsMins: londonTime.time,
    suffix: londonTime.suffix,
  };
};

const requestOptions = {
  method: 'GET',
};

export const onDownload = (url, name) => {
  fetch(url, requestOptions)
    .then((res) => {
      return res.blob();
    })
    .then((blob) => {
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((err) => {
      console.log('Something went wrong', err);
      return Promise.reject({ Error: 'Something Went Wrong', err });
    });
};

export const getHostnameFromRegex = (url) => {
  const matches = url.match(/^https?:\/\/([^/?#]+)(?:[/?#]|$)/i);
  return matches && matches[1];
};

export const toAbsoluteHttpUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^\/\//.test(trimmed)) return `https:${trimmed}`;
  return `https://${trimmed.replace(/^\/+/, '')}`;
};

export const titleCase = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function positiveDurationMinutes(value) {
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

// Stored release and end digits are UK wall-clock time saved as UTC.
// During BST that instant is one hour earlier than the stored digits.
function ukWallClockToUtc(value) {
  const isBST = _momentTz2.default.tz(value, 'Europe/London').isDST();
  return _moment2.default.utc(value).subtract(isBST ? 1 : 0, 'hours');
}

export function getCalendarEventTimes(event) {
  const correctedStart = ukWallClockToUtc(event?.releaseTime);
  const start = correctedStart.isValid() ? correctedStart : _moment2.default();

  const durationMinutes = positiveDurationMinutes(event?.eventDuration)
    ?? positiveDurationMinutes(event?.duration);

  let end;
  if (durationMinutes != null) {
    end = start.clone().add(durationMinutes, 'minutes');
  } else if (event?.endTime) {
    const correctedEnd = ukWallClockToUtc(event.endTime);
    end = correctedEnd.isValid() ? correctedEnd : start.clone().add(60, 'minutes');
  } else {
    end = start.clone().add(60, 'minutes');
  }

  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
  };
}


export const handleGenerateCalendarLink = (type,startTime, endTime, location, title, description) =>{
  const formattedDescription = description.replace(/\n/g, "\\n");

    if(type === 'apple' || type === 'outlook') {
      var calendarUrl = "";
      calendarUrl = ["BEGIN:VCALENDAR", "VERSION:2.0", "BEGIN:VEVENT", "URL:" + '', "DTSTART:" + formatTime(startTime), "DTEND:" + formatTime(endTime), "SUMMARY:" + title, "DESCRIPTION:" + formattedDescription, "LOCATION:" + location, "END:VEVENT", "END:VCALENDAR"].join("\n");
        calendarUrl = encodeURI("data:text/calendar;charset=utf8," + calendarUrl);
        console.log('Calender Link', calendarUrl)
        window.open(calendarUrl, "_blank", "noreferrer");
    } else if(type === 'google') {
      var calendarUrl = "";

      console.log('Start time', startTime, 'End time', endTime )
      calendarUrl = "https://calendar.google.com/calendar/render";
      calendarUrl += "?action=TEMPLATE";
      calendarUrl += "&dates=" + formatTime(startTime);
      calendarUrl += "/" + formatTime(endTime);
      calendarUrl += "&location=" + encodeURIComponent(location);
      
      calendarUrl += "&text=" + encodeURIComponent(title);
      calendarUrl += "&details=" + encodeURIComponent(description); 

      console.log('Calender Link', calendarUrl)
      window.open(calendarUrl, "_blank", "noreferrer");

    } 
}


export const handleCustomCalendarLink_pepco = ()=>{
  let calendarUrl = 'https://outlook.live.com/calendar/0/action/compose?allday=false&body=Please%20join%20Stephan%20Borchert%2C%20Pepco%20Group%20CEO%2C%20and%20other%20leaders%20for%20a%20Group%20Townhall.&enddt=2024-12-11T10%3A00%3A00&location=&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&startdt=2024-12-11T09%3A00%3A00&subject=Pepco%20group%20-%20Townhall'
  window.open(calendarUrl, "_blank", "noreferrer");
}

export const handleGenerateZoomCalendarLink = (type,startTime, endTime, location, title, description) =>{
  const formattedDescription = description.replace(/\n/g, "\\n");

    if(type === 'apple' || type === 'outlook') {
      var calendarUrl = "";
      calendarUrl = ["BEGIN:VCALENDAR", "VERSION:2.0", "BEGIN:VEVENT", "DTSTART:" + formatTime(startTime), "DTEND:" + formatTime(endTime), "SUMMARY:" + title, "DESCRIPTION:" + formattedDescription, "LOCATION:" + "", "END:VEVENT", "END:VCALENDAR"].join("\n");
        calendarUrl = encodeURI("data:text/calendar;charset=utf8," + calendarUrl);
        console.log('Calender Link', calendarUrl)
        window.open(calendarUrl, "_blank", "noreferrer");
    } else if(type === 'google') {
      var calendarUrl = "";

      console.log('Start time', startTime, 'End time', endTime )
      calendarUrl = "https://calendar.google.com/calendar/render";
      calendarUrl += "?action=TEMPLATE";
      calendarUrl += "&dates=" + formatTime(startTime);
      calendarUrl += "/" + formatTime(endTime);
      calendarUrl += "&location=" + encodeURIComponent(location);
      
      calendarUrl += "&text=" + encodeURIComponent(title);
      calendarUrl += "&details=" + encodeURIComponent(description); 

      console.log('Calender Link', calendarUrl)
      window.open(calendarUrl, "_blank", "noreferrer");

    } 
}


function formatTime(date) {
  var formattedDate = _moment2.default.utc(date).format("YYYYMMDDTHHmmssZ");
  return formattedDate.replace("+00:00", "Z");
}

function getRandomKey() {
  var n = Math.floor(Math.random() * 999999999999).toString();
  return new Date().getTime().toString() + "_" + n;
}